import { useState } from 'react';
import { Mail, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useInviteMember } from '@/features/team/hooks/useTeam';
import { RoleSelector } from '@/features/team/components/RoleSelector';
import type { TeamRole } from '@/features/review/types';

// =========================================
// Props
// =========================================

interface InviteModalProps {
  teamId: string;
  teamName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// =========================================
// InviteModal Component
// =========================================

export function InviteModal({
  teamId,
  teamName,
  open,
  onOpenChange,
}: InviteModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamRole>('reviewer');
  const inviteMember = useInviteMember();

  const handleInvite = async () => {
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      toast.error('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      await inviteMember.mutateAsync({
        team_id: teamId,
        email: trimmedEmail,
        role,
      });
      toast.success(`Invitation sent to ${trimmedEmail}`);
      setEmail('');
      setRole('reviewer');
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to send invitation'
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-indigo-600" />
            Invite Member
          </DialogTitle>
          <DialogDescription>
            Invite someone to join <span className="font-medium">{teamName}</span>.
            They must have an account to be invited.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="invite-email">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                id="invite-email"
                type="email"
                placeholder="colleague@university.edu"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                className="pl-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Role</Label>
            <RoleSelector value={role} onChange={setRole} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleInvite}
            disabled={!email.trim() || inviteMember.isPending}
          >
            {inviteMember.isPending ? 'Inviting...' : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
